import {exportColumns} from '../../api';
import {localStorageKeysConstants} from '../../constants/localStorage.constants';

const {EXPORT_COLUMNS} = localStorageKeysConstants;

const ALL_COLUMNS = exportColumns.columns;
const ALL_KEYS = new Set(ALL_COLUMNS.map((column) => column.key));
const LOCKED_COLUMNS = ALL_COLUMNS.filter((column) => column.locked);
const SELECTABLE_COLUMNS = ALL_COLUMNS.filter((column) => !column.locked);
const DEFAULT_KEYS = Object.freeze(SELECTABLE_COLUMNS.filter((column) => column.default).map((column) => column.key));

const sameKeySet = (keys, otherKeys) => {
  if (keys.length !== otherKeys.length) return false;
  const otherSet = new Set(otherKeys);
  return keys.every((key) => otherSet.has(key));
};

/**
 * Reads, writes and resets the export wizard's saved column selection (plan
 * ready-to-use-accounting-exports.md §5 story 7b). `BANKING_STATEMENT_COLUMNS`'s 45 columns
 * split into the 8 always-included (`lockedColumns`) and the 37 a user can pick from
 * (`selectableColumns`); 9 of those 37 are `defaultKeys` -- today's actual file, unchanged.
 *
 * Eligibility (ICP tier, the budgets flag) is not this module's job: it needs React hooks
 * (`useIsIcpTierSmall`, `useIsBudgetsAvailable`) that a plain module cannot call, so the
 * component filters `selectableColumns` for display and for the request. This module stores
 * and returns the user's full selection untouched -- never write a filtered set back here, or
 * a company that loses eligibility for a column (a tier change, a flag toggle) loses it from
 * their saved set for good instead of just until eligibility returns.
 */
export const exportColumnsHelpers = {
  lockedColumns: LOCKED_COLUMNS,
  selectableColumns: SELECTABLE_COLUMNS,
  defaultKeys: DEFAULT_KEYS,

  /** Whether `keys` is exactly the standard default set, order ignored. */
  isDefaultSet: (keys) => sameKeySet(keys, DEFAULT_KEYS),

  /** Set equality, order ignored. The component uses this directly too, to compare a selection
   * against the *eligible* default set -- two tier-gated columns among the 9 are unavailable to
   * a small-tier company, which this module cannot compute itself (see the note above). */
  sameKeySet,

  /**
   * The saved set for this company, or the standard default when nothing is saved for it (or
   * storage is unreadable). Drops any key the contract no longer declares -- a column removed
   * upstream since this was last saved -- rather than sending a key the backend would 422 on
   * (plan §5 story 6: drop on apply with a one-line notice, never send it). `droppedKeys` carries
   * that notice's payload, empty when nothing was dropped.
   */
  getSavedColumns: (companyId) => {
    try {
      const stored = JSON.parse(localStorage.getItem(EXPORT_COLUMNS) || 'null');
      if (!companyId || stored?.companyId !== companyId || !Array.isArray(stored?.keys)) {
        return {keys: DEFAULT_KEYS, droppedKeys: []};
      }
      return {
        keys: stored.keys.filter((key) => ALL_KEYS.has(key)),
        droppedKeys: stored.keys.filter((key) => !ALL_KEYS.has(key)),
      };
    } catch (error) {
      return {keys: DEFAULT_KEYS, droppedKeys: []};
    }
  },

  saveColumns: (companyId, keys) => {
    if (!companyId) return;
    try {
      localStorage.setItem(EXPORT_COLUMNS, JSON.stringify({companyId, keys}));
    } catch (error) {
      // Storage unavailable (private mode, quota): the selection simply isn't remembered.
    }
  },

  resetColumns: () => {
    try {
      localStorage.removeItem(EXPORT_COLUMNS);
    } catch (error) {
      // Nothing to reset when storage is unavailable.
    }
  },
};
